import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import MaxWidth from '@/components/common/layout/MaxWidth';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { HELP_HERO_URL } from '@/constants/urls';
import { NextRouter, useRouter } from '@/hooks/useRouter';
import Filter, { toSeoUrl } from '@/utility/badWordsFilter';

interface IProps {
  title?: string;
  imageUrl?: string;
}
const TITLE = 'We love to help.';

export const useLoadingState = (router: NextRouter) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!inputRef.current) return;
    if (router.query.query === toSeoUrl(inputRef.current.value)) {
      inputRef.current.value = '';
      setLoading(false);
    }
  }, [router.query.query]);
  return { loading, inputRef, setLoading };
};

const Hero: React.FC<IProps> = ({ title = TITLE, imageUrl = HELP_HERO_URL }) => {
  const router = useRouter();
  const { inputRef, loading, setLoading } = useLoadingState(router);

  const intl = useIntl();
  const HeroText = intl.formatMessage({ defaultMessage: 'We love to help.', id: 'Fqpt+Q' });
  const buttonMessage = intl.formatMessage({ defaultMessage: 'Search', id: 'xmcVZ0' });
  const placeholder = intl.formatMessage({
    defaultMessage: 'Ask a question or enter a search term',
    id: 'z4TuIQ',
  });

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !inputRef.current ||
      !inputRef.current.value.length ||
      router.query.query === toSeoUrl(inputRef.current.value)
    ) {
      return;
    }

    const filter = new Filter();
    const input = inputRef.current;
    const query = input.value.trim();
    const cleanQuery = filter.cleanWords(query);
    const qToSeo = toSeoUrl(cleanQuery);
    if (router.query.query !== toSeoUrl(inputRef.current.value)) {
      setLoading(true);
    }
    const path = cleanQuery ? `/help/search/${qToSeo}` : `/help/search`;
    router.push({ pathname: path });
  };

  return (
    <>
      <div className={`flex flex-col justify-between items-center w-full h-96 relative`}>
        <ResponsiveImage
          src={imageUrl}
          alt={title}
          className="absolute object-cover object-center w-full h-full"
        />

        <MaxWidth className="z-10 justify-center h-full sm:px-8 lg:px-12">
          <div className="flex flex-col justify-between lg:flex-row">
            <h2 className="mb-4 text-white h1 lg:mb-0">{HeroText}</h2>
            <form
              className="flex justify-between w-full p-4 bg-white rounded-lg max-w-30"
              onSubmit={handleSearch}>
              <input
                ref={inputRef}
                type="text"
                name="searchTerm"
                className="w-full pl-2 pr-4 text-base text-gray-900 placeholder-gray-500"
                placeholder={placeholder}
                data-testid="search-bar"
              />
              <Button
                variant={EButtonColorVariant.Primary}
                label={buttonMessage}
                type="submit"
                loading={loading}
                data-testid="help-search-btn"
              />
            </form>
          </div>
        </MaxWidth>
      </div>
    </>
  );
};

export default Hero;
